import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Close,
} from '@mui/icons-material';
import { paymentTermAPI } from '../../services/api';
import { toast } from 'react-toastify';

/**
 * PaymentTermSelect - Reusable payment term select with "Add new" functionality
 * 
 * Props:
 * - value: payment term ID
 * - onChange: callback with selected payment term ID
 * - label: optional label text
 * - paymentTerms: optional pre-loaded payment terms array
 * - onPaymentTermsChange: callback when payment terms list changes
 */
const PaymentTermSelect = ({ 
  value, 
  onChange, 
  label = 'Payment Terms',
  paymentTerms: externalPaymentTerms,
  onPaymentTermsChange,
}) => {
  const [paymentTerms, setPaymentTerms] = useState(externalPaymentTerms || []);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPaymentTerm, setNewPaymentTerm] = useState({
    name: '',
    days: '',
    description: '',
  });

  useEffect(() => {
    if (!externalPaymentTerms) {
      fetchPaymentTerms();
    }
  }, [externalPaymentTerms]);

  useEffect(() => {
    if (externalPaymentTerms) {
      setPaymentTerms(externalPaymentTerms);
    }
  }, [externalPaymentTerms]);

  const fetchPaymentTerms = async () => {
    try {
      setLoading(true);
      const response = await paymentTermAPI.getAllForDropdown();
      setPaymentTerms(response.data || []);
    } catch (error) {
      console.error('Failed to load payment terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentTerm = async () => {
    if (!newPaymentTerm.name.trim()) {
      toast.error('Please enter a payment term name');
      return;
    }
    
    try {
      const response = await paymentTermAPI.create({
        name: newPaymentTerm.name,
        days: parseInt(newPaymentTerm.days) || 0,
        description: newPaymentTerm.description || '',
      });
      
      const createdPaymentTerm = response.data.paymentTerm || response.data;
      const updatedPaymentTerms = [...paymentTerms, createdPaymentTerm];
      setPaymentTerms(updatedPaymentTerms);
      
      if (onPaymentTermsChange) {
        onPaymentTermsChange(updatedPaymentTerms);
      }
      
      onChange(createdPaymentTerm._id);
      handleCloseDialog();
      toast.success('Payment term created successfully');
    } catch (error) {
      console.error('Error creating payment term:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment term');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewPaymentTerm({
      name: '',
      days: '',
      description: '',
    });
  };

  const handleChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === '__add_new__') {
      setDialogOpen(true);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
        <Typography sx={{ width: { xs: 'auto', sm: 130 }, minWidth: { sm: 130 }, color: '#666', fontSize: '14px', mb: { xs: 0.5, sm: 0 } }}>
          {label}
        </Typography>
        <TextField
          select
          name="paymentTerms"
          value={value || ''}
          onChange={handleChange}
          variant="standard"
          size="small"
          InputProps={{ disableUnderline: true }}
          sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
        >
          <MenuItem value="">Immediate</MenuItem>
          {paymentTerms.map((term) => (
            <MenuItem key={term._id} value={term._id}>
              {term.name}
            </MenuItem>
          ))}
          <MenuItem 
            value="__add_new__"
            sx={{ 
              color: '#875A7B', 
              fontWeight: 500,
              borderTop: '1px solid #e0e0e0',
              mt: 1,
            }}
          >
            <Add sx={{ mr: 1, fontSize: 18 }} />
            Create New Payment Term
          </MenuItem>
        </TextField>
      </Box>

      {/* Add Payment Term Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
            Create New Payment Term
          </Typography>
          <IconButton size="small" onClick={handleCloseDialog} sx={{ color: '#666' }}>
            <Close sx={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Payment Term Name"
              value={newPaymentTerm.name}
              onChange={(e) => setNewPaymentTerm({ ...newPaymentTerm, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Net 60 Days"
              size="small"
            />
            <TextField
              label="Number of Days"
              type="number"
              value={newPaymentTerm.days}
              onChange={(e) => setNewPaymentTerm({ ...newPaymentTerm, days: e.target.value })}
              fullWidth
              placeholder="e.g., 60"
              size="small"
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Description (Optional)"
              value={newPaymentTerm.description}
              onChange={(e) => setNewPaymentTerm({ ...newPaymentTerm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Additional details about this payment term"
              size="small"
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddPaymentTerm}
            sx={{ 
              backgroundColor: '#875A7B',
              '&:hover': { backgroundColor: '#714B68' }
            }}
          >
            Create Payment Term
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentTermSelect;
